/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import "@material/web/button/outlined-button";

import { Overlay } from "@angular/cdk/overlay";
import { AsyncPipe, NgClass, NgStyle } from "@angular/common";
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from "@angular/core";
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router, RouterModule } from "@angular/router";
import {
  catchError,
  map,
  Observable,
  of,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap,
} from "rxjs";

import { DialogWrapperComponent } from "../dialog-wrapper/dialog-wrapper.component";
import { DeliveryService } from "../services/delivery.service";
import { QPilotCloudApiService } from "../services/qpilot-cloud-api.service";
import { SiteSettingsService } from "../services/site-settings.service";
import {
  DeliveryDateUpdateEvent,
  errorSnackBar,
  successSnackBar,
} from "../utils";
import { DataService } from "./../services/data.service";
import { ViewportService } from "../services/viewport.service";
import moment from "moment";
import { ScheduledOrderCouponComponent } from "../web-components/scheduled-order-coupon/scheduled-order-coupon.component";
import { ScheduledOrderDetailsProductComponent } from "../web-components/scheduled-order-details-product/scheduled-order-details-product.component";
import { ScheduledOrderDetailsComponent } from "../web-components/scheduled-order-details/scheduled-order-details.component";
import { ScheduledOrderPaymentMethodComponent } from "../web-components/scheduled-order-payment-method/scheduled-order-payment-method.component";
import { ScheduledOrderShippingComponent } from "../web-components/scheduled-order-shipping/scheduled-order-shipping.component";
import { ScheduledOrderSummaryComponent } from "../web-components/scheduled-order-summary/scheduled-order-summary.component";
import { AddNoteComponent } from "../web-components/add-note/add-note.component";
import {
  UpdateOrderItemQuantityEvent,
  ChangeOrderItemQuantityEvent,
  DeleteOrderItemEvent,
  PauseOrderEvent,
  CancelOrderEvent,
  ResumeOrderEvent,
  DeleteCouponEvent,
  RescheduleOrderEvent,
} from "../web-components/types/events";
import {
  QPilotScheduledOrder,
  QPilotScheduledOrderTotals,
  QPilotScheduledOrderAmountDetail,
  QPilotCalculateTotalsRequest,
  OrderStatus,
} from "../web-components/types/scheduled-order";
import { QPilotScheduledOrderItem } from "../web-components/types/scheduled-order-item";
import { BundlesCardComponent } from "../web-components/bundles/bundles-card/bundles-card.component";
import { IBundle } from "../web-components/bundles/bundle.interfaces";

export type Order = QPilotScheduledOrder & {
  getOrderTotals: () => Observable<QPilotScheduledOrderTotals>;
  closestNextDeliveryDate?: string;
  bundleId?: number;
  bundleName?: string;
};
@Component({
  selector: "app-order-detail-view",
  standalone: true,
  imports: [
    ScheduledOrderDetailsComponent,
    ScheduledOrderDetailsProductComponent,
    ScheduledOrderShippingComponent,
    ScheduledOrderSummaryComponent,
    AddNoteComponent,
    RouterModule,
    AsyncPipe,
    MatDialogModule,
    ScheduledOrderCouponComponent,
    ScheduledOrderPaymentMethodComponent,
    NgClass,
    NgStyle,
    BundlesCardComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: "./order-detail-view.component.html",
  styleUrl: "./order-detail-view.component.scss",
})
export class OrderDetailViewComponent implements OnInit, OnDestroy {
  order$: Observable<Order>;
  orderTotals$: Observable<QPilotScheduledOrderTotals>;
  discounts$: Observable<QPilotScheduledOrderAmountDetail[]>;
  itemsCount$: Observable<number>;
  orderVerboseName: string;
  orderVerboseNamePlural: string;
  nextOccurrenceVerboseName: string;
  dialogRef: MatDialogRef<DialogWrapperComponent>;
  alignActionsToRight = true;
  processingStatusList = ["processing", "queued"];
  showCoupons: boolean = true;
  hideNotesComponent: boolean = false;
  isProcessing$: Observable<{ value: boolean }>;
  closestNextOccurrenceDate: string;

  // Array of components to be rendered in order
  orderedComponents: { identifier: string; order: number }[] = [];

  @Input() orderId: string;
  @ViewChild(ScheduledOrderDetailsComponent)
  detailsCoreComponent: ScheduledOrderDetailsComponent;
  bundle: IBundle;
  private getBundleByIdSubscription: Subscription;
  constructor(
    private dataService: DataService,
    private qPilotCloudApiService: QPilotCloudApiService,
    protected siteSettingsService: SiteSettingsService,
    protected deliveryService: DeliveryService,
    protected viewPortService: ViewportService,
    private snackBar: MatSnackBar,
    private router: Router,
    private dialog: MatDialog,
    private overlay: Overlay,
    private ref: ElementRef,
    private renderer: Renderer2,
  ) {
    const {
      orderVerboseName,
      orderVerboseNamePlural,
      nextOccurrenceVerboseName,
      showCoupons,
    } = this.siteSettingsService;
    this.orderVerboseName = orderVerboseName;
    this.orderVerboseNamePlural = orderVerboseNamePlural;
    this.nextOccurrenceVerboseName = nextOccurrenceVerboseName;
    this.showCoupons = showCoupons;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.closestNextOccurrenceDate =
      this.router.getCurrentNavigation()?.extras?.state?.[
        "closestNextOccurrenceDate"
      ];
  }
  ngOnDestroy(): void {
    if (this.getBundleByIdSubscription) {
      this.getBundleByIdSubscription.unsubscribe();
    }
  }

  /**
   * Sets up the ordered components array based on site settings
   */
  private setupOrderedComponents(): void {
    const config =
      this.siteSettingsService.siteSettings.detailedSummaryComponentConfig;

    if (!config?.components || !config.components.length) {
      // Default order if no configuration is available
      this.orderedComponents = [
        { identifier: "detailed-summary", order: 0 },
        { identifier: "add-product", order: 1 },
        { identifier: "payment-method", order: 2 },
        {
          identifier: "shipping-delivery",
          order: 3,
        },
        {
          identifier: "change-shipping-address",
          order: 4,
        },
        { identifier: "coupons", order: 5 },
        { identifier: "update-coupon", order: 6 },
        {
          identifier: "order-totals-summary",
          order: 7,
        },
        { identifier: "order-notes", order: 8 },
      ];
      return;
    }

    // Filter enabled components and sort by order
    this.orderedComponents = config.components
      .filter((c) => c.enabled)
      .sort((a, b) => a.order - b.order);

    console.log("Ordered components:", this.orderedComponents);
  }

  ngOnInit() {
    // Set up ordered components
    this.setupOrderedComponents();

    this.dataService.setCurrentOrderId(parseInt(this.orderId));
    this.order$ = this.dataService.getOrder(parseInt(this.orderId)).pipe(
      map((order) => {
        const calculateTotalsRequest = this.calculateTotalsRequest(order);

        const useNextime = this.siteSettingsService.useNextime;
        if (
          useNextime &&
          order.status == "active" &&
          !order.estimatedDeliveryDate
        ) {
          this.deliveryService.trackOrder(order.id);
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const orderWithShippingOptions = order as any;
        if (
          orderWithShippingOptions.ShippingRateOptions &&
          orderWithShippingOptions.ShippingRateOptions.length > 0
        ) {
          const option = orderWithShippingOptions.ShippingRateOptions[0];
          if (option.ShippingLines && option.ShippingLines.length > 0) {
            const shippingLine = option.ShippingLines[0];
            if (
              shippingLine.SourceShippingRate &&
              shippingLine.SourceShippingRate.Name
            ) {
              order.shippingRateName = shippingLine.SourceShippingRate.Name;
            } else if (shippingLine.Name) {
              order.shippingRateName = shippingLine.Name;
            } else if (shippingLine.name) {
              order.shippingRateName = shippingLine.name;
            } else if (option.OptionText) {
              order.shippingRateName = option.OptionText;
            } else if (option.OptionValue) {
              order.shippingRateName = option.OptionValue;
            }

            if (shippingLine.Total) {
              order.shippingTotal = shippingLine.Total;
            } else if (shippingLine.total) {
              order.shippingTotal = shippingLine.total;
            }
          }
        }

        this.hideNotesComponent = !(
          this.siteSettingsService.canUpdateNotes || order.note != null
        );

        const cachedTotals$: Observable<QPilotScheduledOrderTotals> =
          this.qPilotCloudApiService
            .calculateScheduledOrderTotals(calculateTotalsRequest)
            .pipe(shareReplay({ refCount: true, bufferSize: 1 }));

        return {
          ...order,
          status: order.locked ? "processing" : order.status,
          closestNextOccurrenceDate: this.closestNextOccurrenceDate,
          getOrderTotals: () => cachedTotals$,
        };
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.isProcessing$ = this.order$.pipe(
      map((order) => ({
        value: order.locked || this.processingStatusList.includes(order.status),
      })),
    );

    this.orderTotals$ = this.order$.pipe(
      switchMap((order) => order.getOrderTotals()),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.discounts$ = this.orderTotals$.pipe(
      map((orderTotals) => {
        const couponDiscounts = orderTotals?.itemsTotals?.couponDiscounts ?? [];
        const shippingCouponDiscounts =
          orderTotals?.shippingTotals?.couponDiscounts ?? [];
        return [...couponDiscounts, ...shippingCouponDiscounts];
      }),
    );

    this.itemsCount$ = this.order$.pipe(
      map(
        (order) =>
          order.scheduledOrderItems?.reduce(
            (a, b) => a + (b.quantity ?? 0),
            0,
          ) ?? 0,
      ),
    );

    this.order$.pipe(take(1)).subscribe((order) => {
      if (order.bundleId) {
        this.getBundleByIdSubscription = this.qPilotCloudApiService
          .getBundleById(order.bundleId)
          .pipe(take(1))
          .subscribe({
            next: (data) => {
              this.bundle = data;
            },
            error: (error) => {
              console.error("Error fetching bundle:", error);
            },
          });
      }
    });
    this.alignActionsToRight = this.ref.nativeElement.offsetWidth < 600;
  }

  @HostListener("window:resize", ["$event"])
  onWindowResize() {
    this.alignActionsToRight = this.ref.nativeElement.offsetWidth < 600;
  }

  public get isDetailedSummaryEnabled(): boolean {
    const config =
      this.siteSettingsService.siteSettings.detailedSummaryComponentConfig;
    return !!config?.components?.find(
      (c) => c.identifier === "detailed-summary",
    )?.enabled;
  }

  public get isAddProductEnabled(): boolean {
    const config =
      this.siteSettingsService.siteSettings.detailedSummaryComponentConfig;
    return !!config?.components?.find((c) => c.identifier === "add-product")
      ?.enabled;
  }

  public get isPaymentMethodEnabled(): boolean {
    const config =
      this.siteSettingsService.siteSettings.detailedSummaryComponentConfig;
    return !!config?.components?.find((c) => c.identifier === "payment-method")
      ?.enabled;
  }

  public get isShippingDeliveryEnabled(): boolean {
    const config =
      this.siteSettingsService.siteSettings.detailedSummaryComponentConfig;
    return !!config?.components?.find(
      (c) => c.identifier === "shipping-delivery",
    )?.enabled;
  }

  public get isChangeShippingAddressEnabled(): boolean {
    const config =
      this.siteSettingsService.siteSettings.detailedSummaryComponentConfig;
    return !!config?.components?.find(
      (c) => c.identifier === "change-shipping-address",
    )?.enabled;
  }

  public get isCouponsEnabled(): boolean {
    const config =
      this.siteSettingsService.siteSettings.detailedSummaryComponentConfig;
    return !!config?.components?.find((c) => c.identifier === "coupons")
      ?.enabled;
  }

  public get isUpdateCouponsEnabled(): boolean {
    const config =
      this.siteSettingsService.siteSettings.detailedSummaryComponentConfig;
    return !!config?.components?.find((c) => c.identifier === "update-coupon")
      ?.enabled;
  }

  public get isOrderTotalSummaryEnabled(): boolean {
    const config =
      this.siteSettingsService.siteSettings.detailedSummaryComponentConfig;
    return !!config?.components?.find(
      (c) => c.identifier === "order-totals-summary",
    )?.enabled;
  }
  public get isOrderNotesEnabled(): boolean {
    const config =
      this.siteSettingsService.siteSettings.detailedSummaryComponentConfig;
    return !!config?.components?.find((c) => c.identifier === "order-notes")
      ?.enabled;
  }

  onUpdateQuantity(
    { quantityToUpdate }: UpdateOrderItemQuantityEvent,
    orderItem: QPilotScheduledOrderItem,
  ) {
    const updateData: QPilotScheduledOrderItem = {
      ...JSON.parse(JSON.stringify(orderItem)),
      quantity: quantityToUpdate,
    };

    this.qPilotCloudApiService
      .updateScheduledOrderItem(updateData)
      .pipe(
        tap(() => {
          if (this.siteSettingsService.useNextime) {
            this.deliveryService.setShouldFetchNextOccurrenceDate(false);
          }

          this.dataService.updateItemOfOrder(
            parseInt(this.orderId),
            updateData,
            this.siteSettingsService.useNextime,
          );
        }),
        tap(() => successSnackBar(this.snackBar, "Quantity updated")),
        catchError(() => {
          errorSnackBar(this.snackBar, "Failed: Error updating quantity");
          return of(null);
        }),
      )
      .subscribe();
  }

  onChangeQuantity($event: ChangeOrderItemQuantityEvent) {
    this.dataService
      .getCurrentOrder()
      .subscribe((scheduledOrder: QPilotScheduledOrder) => {
        const items = scheduledOrder.scheduledOrderItems!.map((item) => ({
          ...item,
        }));
        items.forEach((item) => {
          if (item.id == $event.orderItemId) {
            item.quantity = $event.quantityToUpdate;
          }
        });
      });
  }

  calculateTotalsRequest(scheduledOrder: QPilotScheduledOrder) {
    const calculateTotalsRequest: QPilotCalculateTotalsRequest = {
      scheduledOrderId: scheduledOrder.id,
      customer: scheduledOrder.customer,
      scheduledOrderItems: scheduledOrder.scheduledOrderItems,
      scheduledOrderCoupons: scheduledOrder.couponsHistory,
      coupons: scheduledOrder.coupons,
      shippingCity: scheduledOrder.shippingCity,
      shippingCountry: scheduledOrder.shippingCountry,
      shippingPostcode: scheduledOrder.shippingPostcode,
      shippingState: scheduledOrder.shippingState,
      shippingStreet1: scheduledOrder.shippingStreet1,
      shippingStreet2: scheduledOrder.shippingStreet2,
      currencyIso: scheduledOrder.currencyIso,
      cycles: scheduledOrder.cycles!,
      total: scheduledOrder.total,
      preferredShippingRateOption: scheduledOrder.preferredShippingRateOption,
      frequencyType: scheduledOrder.frequencyType,
      frequency: scheduledOrder.frequency,
    };

    return calculateTotalsRequest;
  }

  onDeleteOrderItem({ orderItemId }: DeleteOrderItemEvent) {
    this.qPilotCloudApiService
      .deleteScheduledOrderItem(orderItemId)
      .pipe(
        tap(() => {
          if (this.siteSettingsService.useNextime) {
            this.deliveryService.setShouldFetchNextOccurrenceDate(false);
          }

          this.dataService.deleteItemOfOrder(
            parseInt(this.orderId),
            orderItemId,
            this.siteSettingsService.useNextime,
          );
        }),
        tap(() => successSnackBar(this.snackBar, "Product deleted")),
        catchError(() => {
          errorSnackBar(this.snackBar, "Failed: Error deleting Product");
          return of(null);
        }),
      )
      .subscribe();
  }

  onEditOrderItem(orderItem: QPilotScheduledOrderItem) {
    this.qPilotCloudApiService
      .getProducts()
      .pipe(
        map((data) => data.items),
        tap((data) => this.dataService.setProducts(data)),
        tap(() =>
          this.openDialog("add-products", false, {
            productId: orderItem.productId ?? orderItem.product?.id,
          }),
        ),
      )
      .subscribe();
  }

  onProcessOrder(order: Order) {
    const closestNextDeliveryDate = order.closestNextDeliveryDate;
    this.qPilotCloudApiService
      .retryProcessing(order.id)
      .pipe(
        tap((updatedOrder) =>
          this.dataService.updateOrder({
            ...order,
            status: updatedOrder.status.toLowerCase() as OrderStatus,
            lastOccurrenceUtc: updatedOrder.lastOccurrenceUtc,
            nextOccurrenceUtc: updatedOrder.nextOccurrenceUtc,
            estimatedDeliveryDate: closestNextDeliveryDate,
          }),
        ),
        tap(() =>
          successSnackBar(
            this.snackBar,
            `${this.orderVerboseName} is Processing`,
          ),
        ),
        catchError(() => {
          errorSnackBar(
            this.snackBar,
            `Failed: Error trying to process ${this.orderVerboseName}`,
          );
          return of(null);
        }),
      )
      .subscribe();
  }

  onPauseOrder({ orderId }: PauseOrderEvent) {
    this.qPilotCloudApiService
      .changeScheduledOrderStatus(orderId, "paused")
      .pipe(
        tap(() => this.dataService.pauseOrder(orderId)),
        tap(() => successSnackBar(this.snackBar, "Order paused")),
        catchError(() => {
          errorSnackBar(this.snackBar, "Failed: Error pausing Order");
          return of(null);
        }),
      )
      .subscribe();
  }

  onCancelOrder({ orderId }: CancelOrderEvent) {
    this.qPilotCloudApiService
      .deleteScheduledOrder(orderId)
      .pipe(
        tap(() => this.dataService.deleteOrder(orderId)),
        tap(() => successSnackBar(this.snackBar, "Order deleted")),
        catchError(() => {
          errorSnackBar(this.snackBar, "Failed: Error deleting Order");
          return of(null);
        }),
      )
      .subscribe();
  }

  onResumeOrder({ orderId }: ResumeOrderEvent) {
    this.qPilotCloudApiService
      .safeActivateScheduledOrder(orderId)
      .pipe(
        tap(() => this.dataService.resumeOrder(orderId)),
        tap(() => successSnackBar(this.snackBar, "Order reactivated")),
        catchError(() => {
          errorSnackBar(this.snackBar, "Failed: Error reactivating Order");
          return of(null);
        }),
      )
      .subscribe();
  }

  onDeleteCoupon(
    { couponCode }: DeleteCouponEvent,
    scheduledOrder: QPilotScheduledOrder,
  ) {
    const updatedScheduledOrder: QPilotScheduledOrder = {
      ...scheduledOrder,
      coupons: [
        ...(scheduledOrder.coupons ?? []).filter((code) => code != couponCode),
      ],
    };

    this.qPilotCloudApiService
      .updateScheduledOrder(updatedScheduledOrder)
      .pipe(
        tap((result) => this.dataService.updateOrder(result)),
        tap(() => successSnackBar(this.snackBar, "Coupon deleted")),
        catchError(() => {
          errorSnackBar(this.snackBar, "Failed: Error deleting coupon");
          return of(null);
        }),
      )
      .subscribe();
  }

  updateNOD(event: RescheduleOrderEvent) {
    if (this.siteSettingsService.useNextime)
      this.deliveryService.isLoading = true;

    this.qPilotCloudApiService
      .updateScheduledOrderNextOccurrence(
        event.orderId,
        new Date(event.nextOccurrenceUtc),
      )
      .pipe(
        tap((order) => {
          if (this.siteSettingsService.useNextime) {
            this.deliveryService.setShouldFetchNextOccurrenceDate(false);
          }
          this.dataService.updateOrderNextOccurrenceAndDeliveryInfo(
            order.id,
            order.nextOccurrenceUtc,
            undefined,
          );
        }),
        tap(() =>
          successSnackBar(this.snackBar, "Update: Next Order Date Changed"),
        ),
        catchError(() => {
          errorSnackBar(
            this.snackBar,
            "Update Failed: Error Updating Next Order Date",
          );
          return of(null);
        }),
      )
      .subscribe();
  }

  updateClosestDeliveryDate(event: DeliveryDateUpdateEvent, order: Order) {
    const detail = event.detail;
    if (!detail) return;
    const { deliveryDate } = detail;
    if (!deliveryDate) return;

    const utcOffset = this.siteSettingsService.siteSettings.utcOffset ?? 0;
    const deliveryDateObj = moment(deliveryDate)
      .utcOffset(utcOffset, true)
      .toDate();
    const deliveryDateUtc = moment(deliveryDateObj)
      .utc()
      .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    order.closestNextDeliveryDate = deliveryDateUtc;
  }

  async openDeliveryDateDialog() {
    await this.detailsCoreComponent.openDeliveryDateDialog();
  }

  openDialog(
    routeName: string,
    showCloseButton: boolean,
    args?: {
      productId?: string | number;
      discounts?: QPilotScheduledOrderAmountDetail[];
      resumeAfter?: boolean;
      dialogMaxWidth?: string;
    },
    order?: Order | null,
  ) {
    const vw = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0,
    );
    const isMobileView = vw < 600;
    const config = isMobileView
      ? {
          maxWidth: "100vw",
          width: "100%",
          maxHeight: "100vh",
          height: "100%",
          scrollStrategy: this.overlay.scrollStrategies.noop(),
        }
      : {
          maxWidth: args?.dialogMaxWidth
            ? `min(calc(100vw - 100px), ${args.dialogMaxWidth})`
            : "min(calc(100vw - 100px), 700px)",
          maxHeight: "calc(100vh - 150px)",
          width: "80%",
          scrollStrategy: this.overlay.scrollStrategies.noop(),
        };
    let isNextOccurrenceInPast = false;
    let confirmButtonText = "Confirm";

    if (order) {
      const nextOccurrence = new Date(order.nextOccurrenceUtc);
      const today = new Date();
      isNextOccurrenceInPast = nextOccurrence < today;
      confirmButtonText = "Reschedule and Confirm";
    }

    this.dialogRef = this.dialog.open(DialogWrapperComponent, {
      ...config,
      data: {
        showCloseButton,
        orderId: parseInt(this.orderId),
        order: this.order$,
        orderTotals: this.orderTotals$,
        discounts: this.discounts$,
        resumeAfter: args?.resumeAfter,
        isMobileView: isMobileView, // Pass the mobile view status
        confirmButtonText: confirmButtonText,
        orderVerboseName: this.orderVerboseName,
        dateInThePast: isNextOccurrenceInPast,
      },
    });

    this.dialogRef
      .afterOpened()
      .pipe(
        tap(() => {
          void this.router.navigate(
            [
              {
                outlets: {
                  dialog: args?.productId
                    ? [routeName, args?.productId]
                    : [routeName],
                },
              },
            ],
            {
              skipLocationChange: true,
            },
          );
        }),
      )
      .subscribe();

    this.dialogRef
      .afterClosed()
      .pipe(
        tap(() => {
          void this.router.navigate([{ outlets: { dialog: null } }], {
            skipLocationChange: true,
          });
          // Check if the closed dialog was the update-bundle dialog
          if (routeName === "update-bundle") {
            this.ngOnInit();
          }
        }),
      )
      .subscribe();
  }

  openDialogAddProducts() {
    this.qPilotCloudApiService
      .getProducts()
      .pipe(
        map((data) => data.items),
        tap((data) => this.dataService.setProducts(data)),
        tap(() => this.openDialog("add-products", false)),
      )
      .subscribe();
  }

  openDialogUpdateBundle() {
    this.openDialog("update-bundle", false, {
      dialogMaxWidth: "1200px",
    });
  }

  goBack() {
    void this.router.navigate(["orders"], { skipLocationChange: true });
  }
}
